import React, {useCallback, useEffect, useState} from 'react';
import {useGlobal} from 'reactn';
import {hot} from 'react-hot-loader/root';
import History from '../components/History';
import TestResults from '../components/TestResults';
import {CONFIG, I18N} from '../globals/config';
import debounce from 'lodash/debounce';
import useCheckboxes from '../hooks/useCheckboxes';
import {submit} from '../api/ajax/update-urls';
import ProgressBar from '../components/url-fields/ProgressBar';
import {formatCounted} from '../helpers/format';
import ErrorBoundary from '../components/ErrorBoundary';

import styles from './url-fields.pcss';


const UrlFields = () => {
	const [ testsModelOpen, setTestsModelOpen ] = useState( false );
	const [ historyModelOpen, setHistoryModelOpen ] = useState( false );
	const [ testsTooltipOpen, setTestsTooltipOpen ] = useState( false );
	const [ historyTooltipOpen, setHistoryTooltipOpen ] = useState( false );
	const [ oldUrl, setOldUrl ] = useGlobal( 'oldUrl' );
	const [ newUrl, setNewUrl ] = useGlobal( 'newUrl' );

	const [ foundCount, setFoundCount ] = useState( 0 );
	const [ updated, setUpdated ] = useState( false );
	const [ finished, setFinished ] = useState( false );
	const {hasChecked, totalCount, lastChecked, updating, setUpdating} = useCheckboxes();

	const submitDisabled = ! hasChecked || updating || ( '' === oldUrl || '' === newUrl );
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const countUrls = useCallback( debounce( async data => {
		if ( data.updating || '' === data.oldUrl || '' === data.newUrl ) {
			return;
		}
		setUpdated( false );
		setFoundCount( 0 );
		setFoundCount( await submit() );
	}, 2000 ), [ setUpdated, setFoundCount ] );

	useEffect( () => {
		if ( updating ) {
			return;
		}
		countUrls( {oldUrl, newUrl, updating} );
		/**
		 * Recount when the entered url changes or checkboxes are checked.
		 */
	}, [ oldUrl, newUrl, countUrls, lastChecked ] ); // eslint-disable-line

	const updateUrls = async() => {
		setUpdating( true );
		setUpdated( false );
		setFinished( false );
		await submit( false );
		setFinished( true );
		// Give progress bar time to show 100%.
		setTimeout( () => {
			setUpdating( false );
			setUpdated( true );
		}, 1000 );
	};


	return (
		<ErrorBoundary>
			<table className={'form-table ' + styles.wrap}>
				<tbody>
					<tr>
						<th scope="row">
							<label htmlFor={'go-live-update-urls/components/url-fields/old-url'}>
								{I18N.oldUrl}
							</label>
						</th>
						<td className={styles.input}>
							<input
								type="text"
								id="go-live-update-urls/components/url-fields/old-url"
								value={oldUrl}
								className={'regular-text'}
								title={I18N.oldUrl}
								readOnly={updating}
								onChange={ev => setOldUrl( ev.target.value )} />
						</td>
						<td className={styles.history}>
							{historyTooltipOpen && <div className={styles.tooltip}>
								{I18N.history_description}
							</div>}
							<button
								className="dashicons dashicons-backup button-primary"
								onFocus={() => setHistoryTooltipOpen( true )}
								onMouseOver={() => setHistoryTooltipOpen( true )}
								onMouseOut={() => setHistoryTooltipOpen( false )}
								onBlur={() => setHistoryTooltipOpen( false )}
								onClick={ev => {
									ev.preventDefault();
									setHistoryModelOpen( true );
								}}
								id="go-live-update-urls/components/url-fields/history" />
						</td>
					</tr>
					<tr>
						<th scope="row">
							<label htmlFor={'go-live-update-urls/components/url-fields/new-url'}>
								{I18N.newUrl}
							</label>
						</th>
						<td className={styles.input}>
							<input
								type="text"
								id="go-live-update-urls/components/url-fields/new-url"
								value={newUrl}
								className={'regular-text'}
								title={I18N.newUrl}
								readOnly={updating}
								onChange={ev => setNewUrl( ev.target.value )} />
						</td>
						<td className={styles.test}>
							{testsTooltipOpen && <div className={styles.tooltip}>
								{I18N.test_button_instructions}
							</div>}
							<button
								className="dashicons
dashicons-shield button-primary"
								disabled={( '' === oldUrl || '' === newUrl )}
								id="go-live-update-urls/components/url-fields/tests"
								onFocus={() => setTestsTooltipOpen( true )}
								onMouseOver={() => setTestsTooltipOpen( true )}
								onMouseOut={() => setTestsTooltipOpen( false )}
								onBlur={() => setTestsTooltipOpen( false )}
								onClick={ev => {
									ev.preventDefault();
									setTestsModelOpen( true );
								}}>
							</button>
						</td>
					</tr>
				</tbody>
			</table>

			{foundCount > 0 && updating &&
			<ProgressBar
				percent={parseInt( ( ( totalCount / foundCount ) * 100 ).toFixed( 0 ) )}
				finished={finished} />}
			{updated && <p
				className={'notice notice-success ' + styles.message}
				dangerouslySetInnerHTML={{__html: formatCounted( I18N.successfullyUpdated, totalCount )}} />}{/* phpcs:ignore WordPressVIPMinimum.JS.DangerouslySetInnerHTML */}
			{! submitDisabled && ! updated && foundCount > 0 && <p
				className={'notice notice-warning ' + styles.message}
				dangerouslySetInnerHTML={{__html: formatCounted( I18N.willUpdate, foundCount )}} />}{/* phpcs:ignore WordPressVIPMinimum.JS.DangerouslySetInnerHTML */}

			<p className="submit">
				<button
					disabled={submitDisabled}
					className={'button button-primary'}
					onClick={ev => {
						ev.preventDefault();
						updateUrls();
					}}>
					{updating &&
					<img src={CONFIG.loading} alt={'Loading'} style={{height: 15, margin: '3px 5px -3px 0'}} />}
					{I18N.updateUrls}
				</button>
			</p>
			<TestResults
				isOpen={testsModelOpen}
				onClose={() => setTestsModelOpen( false )} />
			<History
				isOpen={historyModelOpen}
				onClose={() => setHistoryModelOpen( false )} />
		</ErrorBoundary>
	);
};

export default hot( UrlFields );
